import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Comprehensive meta tags and structured data
 * 
 * Best Practices Implemented:
 * 1. Dynamic meta tags for each page
 * 2. Open Network and Twitter Cards
 * 3. Canonical URLs to prevent duplicate content
 * 4. JSON-LD structured data
 * 5. Proper robots directives
 */
const SEO = ({ 
  title = 'Man & Machine | AI Strategy & Agent Platform with MCP',
  description = 'Transform your business with AI agents and Model Context Protocol (MCP). Discover automation opportunities, build intelligent agents, and connect to any system seamlessly.',
  keywords = 'AI agents, Model Context Protocol, MCP, business automation, AI strategy, workflow automation, enterprise AI, AI consulting, MCP integration, AI data connectivity',
  canonicalUrl = 'https://manandmachine.app',
  ogImage = 'https://manandmachine.app/og-image.jpg',
  ogType = 'website',
  noIndex = false,
  structuredData = null,
  article = null, // For blog posts: { publishedTime, modifiedTime, author, tags }
  breadcrumbs = null, // Array of { name, url }
  faq = null, // Array of { question, answer }
  children
}) => {
  // Default structured data for the organization/application
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Man & Machine",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": description,
    "url": canonicalUrl,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Model Context Protocol (MCP) Integration",
      "AI Agent Builder",
      "Business Process Automation",
      "Universal Data Connectivity",
      "ROI Calculator"
    ]
  };

  // Generate breadcrumb structured data
  const breadcrumbStructuredData = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  } : null;

  // Generate FAQ structured data
  const faqStructuredData = faq ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Help",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // Generate article structured data
  const articleStructuredData = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": ogImage,
    "datePublished": article.publishedTime,
    "dateModified": article.modifiedTime || article.publishedTime,
    "author": {
      "@type": "Person",
      "name": article.author || "Man & Machine Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Man & Machine",
      "url": "https://manandmachine.app"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "keywords": article.tags ? article.tags.join(", ") : keywords
  } : null;

  // Bots directive
  const robotsContent = noIndex 
    ? 'noindex, nofollow' 
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      
      {/* Open Network / Facebook */}
      <meta property="og:type" content={article ? 'article' : ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Man & Machine" />
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific Open Network */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* Canonical URL - Critical for preventing duplicate content issues */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Main Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
      
      {/* Breadcrumb Structured Data */}
      {breadcrumbStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
      )}
      
      {/* FAQ Structured Data */}
      {faqStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      )}
      
      {/* Article Structured Data */}
      {articleStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

export default SEO;
